import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  ListTodo, Plus, Trash2, Edit2, Loader2, CheckCircle, 
  XCircle, Clock, DollarSign, Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHORE_ICONS = ['✨', '🧹', '🍽️', '🛏️', '📚', '🐕', '🌱', '🧺', '🚗', '💪'];

const ChoreCard = ({ assignment, onApprove, onReject, isBanker }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700'
  };

  return (
    <div 
      className={cn(
        "flex flex-col p-3 sm:p-4 rounded-xl border-2 transition-all gap-3",
        assignment.status === 'completed' && isBanker ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
      )}
      data-testid={`chore-assignment-${assignment.id}`}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="text-xl sm:text-2xl shrink-0">{assignment.chore_icon}</span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-800 truncate text-sm sm:text-base">{assignment.chore_name}</p>
          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <span className="text-emerald-600 font-semibold">
              ${assignment.reward_amount.toFixed(2)}
            </span>
            {assignment.customer_name && (
              <span className="text-slate-500 truncate">• {assignment.customer_name}</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between gap-2">
        <Badge className={cn("text-xs", statusColors[assignment.status])}>
          {assignment.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
          {assignment.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
          {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
        </Badge>
        
        {isBanker && assignment.status === 'completed' && (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 hover:bg-red-50 px-2"
              onClick={() => onReject(assignment.id)}
              data-testid={`reject-chore-${assignment.id}`}
            >
              <XCircle className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 px-2"
              onClick={() => onApprove(assignment.id)}
              data-testid={`approve-chore-${assignment.id}`}
            >
              <CheckCircle className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Pay</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Chore Manager for Banker Portal
 */
export const ChoreManager = ({ customers = [], className }) => {
  const [chores, setChores] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choreDialogOpen, setChoreDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingChore, setEditingChore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [choreForm, setChoreForm] = useState({ name: '', reward_amount: '', icon: '✨', description: '' });
  const [assignForm, setAssignForm] = useState({ chore_id: '', customer_id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [choresRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/chores`),
        axios.get(`${API}/chores/assignments`)
      ]);
      setChores(choresRes.data);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch chores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChore = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingChore) {
        await axios.patch(`${API}/chores/${editingChore.id}`, {
          name: choreForm.name,
          reward_amount: parseFloat(choreForm.reward_amount),
          icon: choreForm.icon,
          description: choreForm.description || null
        });
        toast.success('Chore updated!');
      } else {
        await axios.post(`${API}/chores`, {
          name: choreForm.name,
          reward_amount: parseFloat(choreForm.reward_amount),
          icon: choreForm.icon,
          description: choreForm.description || null
        });
        toast.success('Chore created!');
      }
      setChoreDialogOpen(false);
      setEditingChore(null);
      setChoreForm({ name: '', reward_amount: '', icon: '✨', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save chore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteChore = async (choreId) => {
    if (!window.confirm('Delete this chore?')) return;
    try {
      await axios.delete(`${API}/chores/${choreId}`);
      toast.success('Chore deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete chore');
    }
  };

  const handleAssignChore = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/chores/assign`, {
        chore_id: assignForm.chore_id,
        customer_id: assignForm.customer_id
      });
      toast.success('Chore assigned!');
      setAssignDialogOpen(false);
      setAssignForm({ chore_id: '', customer_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign chore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveChore = async (assignmentId) => {
    try {
      await axios.post(`${API}/chores/approve/${assignmentId}`);
      toast.success('Chore approved! Reward paid 💰');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve chore');
    }
  };

  const handleRejectChore = async (assignmentId) => {
    try {
      await axios.post(`${API}/chores/reject/${assignmentId}`);
      toast.info('Chore sent back for redo');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject chore');
    }
  };

  const pendingApproval = assignments.filter(a => a.status === 'completed');
  const activeAssignments = assignments.filter(a => a.status === 'pending');

  return (
    <Card className={cn("shadow-lg", className)} data-testid="chore-manager-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-purple-500" />
              Chore Rewards
            </CardTitle>
            <CardDescription>Assign tasks and reward completion</CardDescription>
          </div>
          <div className="flex gap-2">
            {/* Create Chore Dialog */}
            <Dialog open={choreDialogOpen} onOpenChange={setChoreDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingChore(null);
                    setChoreForm({ name: '', reward_amount: '', icon: '✨', description: '' });
                  }}
                  data-testid="create-chore-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Chore
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="chore-dialog">
                <DialogHeader>
                  <DialogTitle>{editingChore ? 'Edit Chore' : 'New Chore'}</DialogTitle>
                  <DialogDescription>Define a task with a reward</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateChore} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Icon</Label>
                    <div className="flex flex-wrap gap-2">
                      {CHORE_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setChoreForm({ ...choreForm, icon })}
                          className={cn(
                            "w-10 h-10 text-xl rounded-lg border-2 transition-all",
                            choreForm.icon === icon ? "border-purple-500 bg-purple-50" : "border-slate-200"
                          )}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="chore-name">Chore Name</Label>
                    <Input
                      id="chore-name"
                      data-testid="chore-name-input"
                      value={choreForm.name}
                      onChange={(e) => setChoreForm({ ...choreForm, name: e.target.value })}
                      placeholder="e.g., Make bed, Do dishes..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="chore-reward">Reward Amount ($)</Label>
                    <Input
                      id="chore-reward"
                      data-testid="chore-reward-input"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={choreForm.reward_amount}
                      onChange={(e) => setChoreForm({ ...choreForm, reward_amount: e.target.value })}
                      placeholder="1.00"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={submitting}>
                    {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingChore ? 'Update' : 'Create'} Chore
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Assign Chore Dialog */}
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={chores.length === 0 || customers.length === 0}
                  data-testid="assign-chore-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Assign
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="assign-dialog">
                <DialogHeader>
                  <DialogTitle>Assign Chore</DialogTitle>
                  <DialogDescription>Give a child a task to complete</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAssignChore} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Select Chore</Label>
                    <Select value={assignForm.chore_id} onValueChange={(v) => setAssignForm({ ...assignForm, chore_id: v })}>
                      <SelectTrigger data-testid="select-chore">
                        <SelectValue placeholder="Choose a chore" />
                      </SelectTrigger>
                      <SelectContent>
                        {chores.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              <span>{c.icon}</span>
                              <span>{c.name}</span>
                              <span className="text-emerald-600">${c.reward_amount}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign To</Label>
                    <Select value={assignForm.customer_id} onValueChange={(v) => setAssignForm({ ...assignForm, customer_id: v })}>
                      <SelectTrigger data-testid="select-assignee">
                        <SelectValue placeholder="Choose a child" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.username}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={submitting || !assignForm.chore_id || !assignForm.customer_id}>
                    {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Assign Chore
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
        ) : (
          <div className="space-y-4">
            {/* Chore Templates */}
            {chores.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Available Chores</p>
                <div className="flex flex-wrap gap-2">
                  {chores.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1">
                      <span>{c.icon}</span>
                      <span className="text-sm">{c.name}</span>
                      <span className="text-xs text-emerald-600">${c.reward_amount}</span>
                      <button onClick={() => handleDeleteChore(c.id)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Approval */}
            {pendingApproval.length > 0 && (
              <div>
                <p className="text-xs font-medium text-blue-600 mb-2 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Ready for Approval ({pendingApproval.length})
                </p>
                <div className="space-y-2">
                  {pendingApproval.map((a) => (
                    <ChoreCard key={a.id} assignment={a} onApprove={handleApproveChore} onReject={handleRejectChore} isBanker={true} />
                  ))}
                </div>
              </div>
            )}

            {/* Active Assignments */}
            {activeAssignments.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Active Assignments</p>
                <div className="space-y-2">
                  {activeAssignments.map((a) => (
                    <ChoreCard key={a.id} assignment={a} isBanker={true} />
                  ))}
                </div>
              </div>
            )}

            {chores.length === 0 && assignments.length === 0 && (
              <div className="text-center py-6">
                <ListTodo className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No chores yet</p>
                <p className="text-xs text-slate-400">Create a chore template first</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * My Chores List for Customer Portal
 */
export const MyChores = ({ className }) => {
  const [chores, setChores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    fetchChores();
  }, []);

  const fetchChores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/chores/my-chores`);
      setChores(response.data);
    } catch (error) {
      console.error('Failed to fetch chores');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (assignmentId) => {
    setCompleting(assignmentId);
    try {
      await axios.post(`${API}/chores/complete/${assignmentId}`);
      toast.success('Great job! Waiting for parent approval 🎉');
      fetchChores();
    } catch (error) {
      toast.error('Failed to mark complete');
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <Card className={cn("shadow-lg rounded-2xl", className)}>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
        </CardContent>
      </Card>
    );
  }

  if (chores.length === 0) {
    return null; // Don't show if no chores
  }

  return (
    <Card className={cn("shadow-lg rounded-2xl", className)} data-testid="my-chores-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ListTodo className="w-5 h-5 text-purple-500" />
          My Chores
        </CardTitle>
        <CardDescription>Complete tasks to earn rewards!</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {chores.map((chore) => (
            <div 
              key={chore.id}
              className={cn(
                "flex items-center justify-between p-4 rounded-xl border-2 transition-all",
                chore.status === 'pending' ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50"
              )}
              data-testid={`my-chore-${chore.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">{chore.chore_icon}</span>
                <div>
                  <p className="font-medium text-slate-800">{chore.chore_name}</p>
                  <p className="text-sm text-emerald-600 font-semibold flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    {chore.reward_amount.toFixed(2)} reward
                  </p>
                </div>
              </div>
              
              {chore.status === 'pending' ? (
                <Button
                  onClick={() => handleComplete(chore.id)}
                  disabled={completing === chore.id}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid={`complete-chore-${chore.id}`}
                >
                  {completing === chore.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Done!
                    </>
                  )}
                </Button>
              ) : (
                <Badge className="bg-blue-100 text-blue-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Waiting...
                </Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChoreManager;
